import {Component, EventEmitter, forwardRef, Input, OnChanges, OnInit, Output, QueryList, SimpleChanges, ViewChildren} from '@angular/core';
import {NgTemplateOutlet} from '@angular/common';
import {FormControl, FormGroup, FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule} from '@angular/forms';
import {UntilDestroy} from '@ngneat/until-destroy';
import {EMPTY, Subject, Subscription} from 'rxjs';
import {catchError, tap} from 'rxjs/operators';
import {
  AzureStorageDocument,
  UploadedDocumentRef,
  EntityTypes,
  KybAddedParties,
  KybDisplayStatus,
  KybEntityResult,
  KybInitiateCorporatePartyFn,
  KybManualPerson,
  KybPartyIdentifiers,
  KybResult,
  KybKeyPeopleValue,
  KybPersonRoleSelection,
  KybPerson,
  KybRefreshFn,
  KybResultFn,
  KybSetManualModeFn,
  KybSignerEntityView,
  KybValidationResult,
  PartnerValue,
  TrusteeValue,
  kybAddedPartiesToPartyValue,
  kybDigitsOnly,
  kybDisplayStatus,
  kybFindEntityForParty,
  kybHasManualPeopleForEmptyResult,
  kybNormalizePartyDobForSave,
  kybPartyEntryIntegrityErrors,
  kybPartyIdentifiers,
  kybIsAdditionalGuarantor,
  kybIsCorporateParty,
  kybManualEntryRequiresManualMode,
  kybIsSamePerson,
  kybIsPersonSignerEligible,
  kybIsSelectLocked,
  kybIsSelectedByDefault,
  kybIsSoleTraderRoleLocked,
  kybResultFailed,
  kybResultResolved,
  kybSectionHeading,
  kybSectionWarning,
  kybSortKeyPeople,
  kybValidateCompanyKeyPeople,
  kybEmptyPeopleManualEntryReason,
  uniqueIntegrityMessages,
} from '@portal-workspace/grow-shared-library';
import {setupUntilDestroy} from '@portal-workspace/grow-ui-library';
import {AbstractControlValueAccessor} from '../abstract-control-value-accessor';
import {MARK, Mark} from '@portal-workspace/grow-ui-library/mark';
import {MarkDirective} from '../../directives/mark-as-dirty.directive';
import {BusinessNumberSearchFn} from '../business-number-search-component/business-number-search.component';
import {PartnerComponent} from '../individual-and-entity-info-component/partner.component';
import {TrusteeComponent} from '../individual-and-entity-info-component/trustee.component';
import {MessageBoxComponent} from '../message-box/message-box.component';
import {KybEntitySectionComponent} from './kyb-entity-section.component';
import {KybStatusComponent} from './kyb-status.component';
import {ManualKybPeopleComponent} from './manual-kyb-people.component';
import {IndividualIdDocumentUpload} from '../component-utils';

type KybPartyControl = FormControl<PartnerValue | TrusteeValue | null>;

@UntilDestroy({arrayName: 'subscriptions'})
@Component({
  selector: 'kyb-key-people',
  templateUrl: './kyb-key-people.component.html',
  styleUrls: ['./kyb-key-people.component.scss'],
  providers: [
    {provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => KybKeyPeopleComponent), multi: true},
    {provide: MARK, useExisting: forwardRef(() => KybKeyPeopleComponent)},
  ],
  standalone: true,
  imports: [NgTemplateOutlet, FormsModule, ReactiveFormsModule, MarkDirective, PartnerComponent, TrusteeComponent, MessageBoxComponent, KybEntitySectionComponent, KybStatusComponent, ManualKybPeopleComponent],
})
export class KybKeyPeopleComponent extends AbstractControlValueAccessor<KybKeyPeopleValue> implements OnInit, OnChanges, Mark {

  @Input({required: true}) applicationId!: number;
  @Input({required: true}) kybResultFn!: KybResultFn;
  @Input({required: false}) uploadedDocuments: AzureStorageDocument[] | null = null;
  // Backs the Retry inside each waiting panel: pulls the entity's execution from FrankieOne rather
  // than reading back what is stored. Handed straight to kyb-status, exactly as on the company
  // details step, so a stalled corporate party is recovered without starting a billable run.
  @Input({required: false}) kybRefreshFn: KybRefreshFn | null = null;
  @Input({required: false}) kybSetManualModeFn: KybSetManualModeFn | null = null;
  // The result the company details step already waited for. Supplying it skips the initial fetch,
  // so the applicant entity's key people render immediately instead of being read a second time.
  @Input({required: false}) seedResult: KybResult | null = null;
  @Input({required: true}) businessNumberSearchFn!: BusinessNumberSearchFn;
  // Starts a KYB run for a corporate partner/trustee as soon as its ACN/ABN is known.
  @Input({required: false}) kybInitiateCorporatePartyFn: KybInitiateCorporatePartyFn | null = null;
  // Drives heading/warning/selection/add-block for the primary entity instead of the
  // FrankieOne-provided entityType (e.g. step3OnwardsOrganisationType).
  @Input({required: false}) organisationType: EntityTypes | null = null;
  @Input({required: false}) showAssetsAndLiabilities = false;
  /**
   * Whether the step is on screen. The stepper keeps every step's content alive, so without this the
   * applicant's waiting panel would begin its wait when the page loads and a broker who spends a
   * minute on an earlier step would arrive to find the check already described as slow. Only gates
   * that panel; the initial read still happens at init, so an already-completed check renders
   * immediately.
   */
  @Input({required: false}) active = true;

  // Non-blocking warnings (currently sub-50% guarantor coverage). The page shows these as a
  // confirmation popup when Next is pressed rather than inline, because they do not block the step.
  @Output() warningsChange = new EventEmitter<string[]>();
  @Output() idDocumentDelete = new EventEmitter<UploadedDocumentRef>();
  @ViewChildren(ManualKybPeopleComponent) manualPeopleComponents!: QueryList<ManualKybPeopleComponent>;
  @ViewChildren(KybEntitySectionComponent) entitySections!: QueryList<KybEntitySectionComponent>;

  subscriptions: Subscription[] = [];
  markObservable: Subject<boolean> = new Subject<boolean>();

  result: KybResult | null = null;

  // Signer/guarantor rule results per commercialEntityId, recomputed on every emit.
  validationByEntity: Map<number, KybValidationResult> = new Map<number, KybValidationResult>();
  // Errors stay hidden until Next is pressed (which reaches mark() through the stepper's mark
  // trigger), so ticking boxes never raises one. From that point on they update live, because
  // runValidation re-runs on every emit.
  showValidation = false;
  // Why the step cannot be completed, when the blocking condition has no field of its own to
  // highlight. Without it a null emit shows only the stepper's generic "kybKeyPeople required".
  blockingReason: string | null = null;
  // Distinct integrity errors of the partner/trustee rows on screen (a trust as a trustee, a sole
  // trader, or the applicant entity re-entered). Shown as soon as the row is entered, unlike blockingReason, and
  // held apart from it because such a row is never saved, so nothing else would explain the block.
  partyIntegrityErrors: string[] = [];

  // Corporate parties already sent to the initiate endpoint, so retyping an ACN does not re-fire.
  private requestedPartyKeys: Set<string> = new Set<string>();
  // Per-parent row -> signer entity lookups, memoised so the child inputs keep a stable identity.
  private signerLookups: Map<number, (row: FormGroup) => KybSignerEntityView | null> = new Map();

  // Guarantor/Signer selections keyed by stakeholderId.
  private selections: Map<number, KybPersonRoleSelection> = new Map();
  // Left-column select state keyed by stakeholderId.
  private selectedStakeholderIds: Set<number> = new Set<number>();
  // Add Partner / Add Trustee controls per partnership/trust entity, keyed by commercialEntityId
  // (kybResultId is null until the KYB run completes, so it is not a stable key).
  private partyControls: Map<number, { entity: KybEntityResult; control: KybPartyControl }> = new Map();
  // Latest partner/trustee rows per parent commercialEntityId, valid or not. Used to tell a corporate
  // party that is still on screen from one left behind by a removed/replaced row.
  private partyEntries: Map<number, readonly unknown[]> = new Map();
  // Corporate parties still listed in an add-party row, mapped to the entity type that row declared.
  // Refreshed by rebuildPartyControls, i.e. whenever the result or the rows change.
  private corporateParties: Map<number, EntityTypes | null> = new Map();
  // Entities currently using the manual people form. This is broader than Manual KYB: a completed
  // FrankieOne result with no people uses the form without setting the entity's manual-mode flag.
  private manualEntryEntityIds = new Set<number>();
  private manualPeopleByEntity = new Map<number, KybManualPerson[]>();
  private manualValidityByEntity = new Map<number, boolean>();

  kybSectionWarning = kybSectionWarning;
  kybSectionHeading = kybSectionHeading;

  constructor() {
    super();
  }

  ngOnInit(): void {
    setupUntilDestroy(this);
    if (this.seedResult) {
      this.applyResult(this.seedResult);
    } else {
      this.load();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['applicationId'] && !changes['applicationId'].firstChange) ||
      (changes['kybResultFn'] && !changes['kybResultFn'].firstChange)) {
      this.load();
    } else if (changes['seedResult'] && !changes['seedResult'].firstChange && this.seedResult) {
      // The company details step waited the check out after this one was constructed - the stepper
      // keeps every step alive - so its result arrives as an input change rather than at init.
      // Without this the step keeps the pending snapshot it read on load and can never complete.
      this.applyResult(this.seedResult, true);
    } else if (changes['organisationType'] && !changes['organisationType'].firstChange && this.result) {
      // Org type resolved/changed after the result loaded - rebuild the add-party controls so a
      // newly-trust/partnership entity gets its trustee/partner control, then re-seed defaults.
      this.rebuildPartyControls();
      this.seedSelectedDefaults(this.result);
      this.applySoleTraderRoles();
      this.emitValue();
    }
  }

  /**
   * Effective type for a section. The page's org type wins for the primary entity.
   *
   * A partner/trustee added on this screen is typed by the ABN/ACN lookup already done on its own
   * row, which is the authority here: FrankieOne is never asked about a trust or a partnership, so
   * its run carries no type of its own and the commercial entity's own column cannot be relied on to
   * decide which parties to collect.
   */
  effectiveEntityType(entity: KybEntityResult): EntityTypes {
    if (entity.isPrimary && this.organisationType) {
      return this.organisationType;
    }
    return this.corporateParties.get(entity.commercialEntityId) ?? entity.entityType;
  }

  load(preserveUserState = false): void {
    // The pages bind `applicationId ?? 0` and the stepper builds this step before the draft is saved,
    // so 0 means "no application yet"; asking for its KYB fails the originator check.
    if (!this.kybResultFn || !this.applicationId) {
      return;
    }
    this.subscriptions.push(
      this.kybResultFn(this.applicationId).pipe(
        tap(result => this.applyResult(result, preserveUserState)),
      ).subscribe(),
    );
  }

  /**
   * A waiting panel's check finished and handed over the read model it was polling, so the section it
   * belongs to can render its people. `preserveUserState` because another section's checkboxes may
   * already have been ticked while this one was still running.
   */
  onSignerResolved(result: KybResult): void {
    this.applyResult(result, true);
  }

  /**
   * Display status of one section.
   *
   * A trust or partnership is resolved on its type alone, which the ABR lookup already gave us: its
   * key people are the trustees/partners entered on this screen, and no KYB people table is rendered
   * for it at all, so its FrankieOne run holds nothing the section can be waiting for. Gating on that
   * run would hide the entry fields behind a check whose outcome the screen never reads.
   */
  statusFor(entity: KybEntityResult): KybDisplayStatus {
    if (this.isManualEntry(entity)) {
      return 'resolved';
    }
    const entityType = this.effectiveEntityType(entity);
    if (entityType === 'trust' || entityType === 'partnership') {
      return 'resolved';
    }
    return kybDisplayStatus(entity.status);
  }

  isManualEntry(entity: KybEntityResult): boolean {
    return entity.mode === 'MANUAL' || this.manualEntryEntityIds.has(entity.commercialEntityId);
  }

  startManualEntry(entity: KybEntityResult): void {
    if (this.effectiveEntityType(entity) === 'trust' || this.effectiveEntityType(entity) === 'partnership') {
      return;
    }
    // A completed result that returned nobody reuses the people form only. Manual KYB is reserved
    // for the fallback where FrankieOne has not produced a curated result.
    if (!kybManualEntryRequiresManualMode(entity)) {
      this.activateManualEntry(entity);
      return;
    }
    if (this.kybSetManualModeFn) {
      this.subscriptions.push(this.kybSetManualModeFn(
        this.applicationId,
        entity.commercialEntityId,
      ).pipe(
        catchError(() => EMPTY),
      ).subscribe(result => this.applyResult(result, true)));
      return;
    }
    this.activateManualEntry(entity);
  }

  private activateManualEntry(entity: KybEntityResult): void {
    // Local display state. The server independently decides whether isManualKybMode may be set
    // from the presence of an ApplicationKYBResult.
    entity.mode = 'MANUAL';
    this.manualEntryEntityIds.add(entity.commercialEntityId);
    this.manualPeopleByEntity.set(
      entity.commercialEntityId,
      this.manualPeopleByEntity.get(entity.commercialEntityId) ?? entity.manualPeople ?? [],
    );
    this.manualValidityByEntity.set(
      entity.commercialEntityId,
      (this.manualPeopleByEntity.get(entity.commercialEntityId)?.length ?? 0) > 0
        && !!this.manualPeopleByEntity.get(entity.commercialEntityId)?.some(person => person.isSigner),
    );
    this.emitValue();
  }

  startManualEntryById(commercialEntityId: number): void {
    const entity = this.result?.entities.find(candidate => candidate.commercialEntityId === commercialEntityId);
    if (entity) this.startManualEntry(entity);
  }

  startPrimaryManualEntry(): void {
    const primary = this.result?.entities.find(entity => entity.isPrimary);
    if (primary) this.startManualEntry(primary);
  }

  manualPeopleFor(entity: KybEntityResult): KybManualPerson[] {
    return this.manualPeopleByEntity.get(entity.commercialEntityId) ?? entity.manualPeople ?? [];
  }

  isSoleTraderRoleLocked(entity: KybEntityResult): boolean {
    return kybIsSoleTraderRoleLocked(this.effectiveEntityType(entity));
  }

  // The in-progress banner is only true while FrankieOne is still running. A completed empty result
  // that the broker then enters by hand must not claim the check is still looking the business up.
  showManualWaitingMessage(entity: KybEntityResult): boolean {
    return kybDisplayStatus(entity.status) !== 'resolved';
  }

  onManualPeopleChange(commercialEntityId: number, people: KybManualPerson[]): void {
    this.manualPeopleByEntity.set(commercialEntityId, people);
    this.emitValue();
  }

  onManualValidityChange(commercialEntityId: number, valid: boolean): void {
    this.manualValidityByEntity.set(commercialEntityId, valid);
    this.emitValue();
  }

  manualIdDocumentUploads(): IndividualIdDocumentUpload[] {
    return this.manualPeopleComponents?.toArray().flatMap(component => component.idDocumentUploads()) ?? [];
  }

  /**
   * Gate for the whole step. Keyed off the primary entity so a pending corporate party no longer
   * blanks the screen and discards the partner/trustee form the user is editing.
   */
  primaryStatus(): KybDisplayStatus {
    const primary = this.result?.entities.find(entity => entity.isPrimary);
    // No primary entity persisted at all: fall back to the overall gate so the placeholder shows.
    return primary ? this.statusFor(primary) : this.computeDisplayStatus(this.result);
  }

  // Names the business in the applicant's waiting panel. Null before anything has been read, which
  // the panel renders as "this business".
  get primaryEntityName(): string | null {
    return (this.result?.entities.find(entity => entity.isPrimary)?.entityName ?? '').trim() || null;
  }

  hasPrimaryEntity(): boolean {
    return !!this.result?.entities.some(entity => entity.isPrimary);
  }

  // Corporate partners/trustees are rendered inside their parent's add-party rows, not as their own
  // top-level section, so their people appear once - under the row that introduced them. Additional
  // guarantors are dropped for a different reason: this screen is the borrowing entity's signer
  // selection, and a guarantor's people are reviewed on the Additional Guarantors step off the same
  // read. The filter sits here rather than in readKybResult because that read feeds both screens.
  topLevelEntities(): KybEntityResult[] {
    return (this.result?.entities ?? [])
      .filter(entity => !kybIsCorporateParty(entity) && !kybIsAdditionalGuarantor(entity));
  }

  signerEntityLookupFor(parent: KybEntityResult): (row: FormGroup) => KybSignerEntityView | null {
    let lookup = this.signerLookups.get(parent.commercialEntityId);
    if (!lookup) {
      lookup = (row: FormGroup) => this.resolveSignerEntity(parent, row);
      this.signerLookups.set(parent.commercialEntityId, lookup);
    }
    return lookup;
  }

  // Match a partner/trustee row to the KYB entity created for it, so its signers table shows that
  // company's own officers rather than the parent trust's/partnership's stakeholders. The status
  // rides along: the entity is matchable from the moment its run is initiated, so without it the
  // signers table would read an in-flight run as a completed one that returned nobody.
  private resolveSignerEntity(parent: KybEntityResult, row: FormGroup): KybSignerEntityView | null {
    const entity = kybFindEntityForParty(
      this.result?.entities ?? [],
      parent.commercialEntityId,
      this.rowIdentifiers(row),
    );
    return entity
      ? {
        entity,
        status: this.statusFor(entity),
        applicationId: this.applicationId,
        pollFn: this.kybResultFn,
        refreshFn: this.kybRefreshFn,
      }
      : null;
  }

  private rowIdentifiers(row: FormGroup): KybPartyIdentifiers {
    return kybPartyIdentifiers({
      manualEntry: !!row.get('manualEntry')?.value,
      stepacn: row.get('stepacn')?.value ?? undefined,
      organisation: row.get('organisation')?.value ?? undefined,
    });
  }

  /**
   * A partner/trustee list changed: start a KYB run for any entity row whose ACN/ABN is now usable.
   * Tracked by identifier so editing an ACN does not fire repeatedly; the endpoint is idempotent as a
   * second line of defence.
   */
  onPartyEvent(parent: KybEntityResult, role: 'partner' | 'trustee', event: { entries: unknown[] }): void {
    this.partyEntries.set(parent.commercialEntityId, event.entries ?? []);
    this.pruneRemovedPartySelections(parent, role, event.entries ?? []);
    // The rows decide which corporate parties are still on screen, which is what makes a trust
    // partner's nested trustee control appear (or disappear with the row that introduced it).
    this.rebuildPartyControls();
    // The rows drive which corporate parties gate the step, so re-emit even when the party control
    // itself has not produced a new value (it stays null while its own form is invalid).
    this.emitValue();
    if (!this.kybInitiateCorporatePartyFn) {
      return;
    }
    for (const entry of event.entries ?? []) {
      const party = entry as {
        type?: string;
        manualEntry?: boolean;
        businessName?: string;
        stepacn?: string;
        organisation?: { name?: string; abn?: string; acn?: string };
        organisationType?: { type?: string };
      };
      if (party?.type !== 'Entity') {
        continue;
      }
      const {abn, acn} = kybPartyIdentifiers(party);
      if (!abn && !acn) {
        continue;
      }
      // An invalid entity is never saved nor sent to FrankieOne; the row is flagged instead.
      if (kybPartyEntryIntegrityErrors(party, role, this.primaryIdentifiers()).length) {
        continue;
      }
      const key = `${parent.commercialEntityId}|${role}|${abn ?? ''}|${acn ?? ''}`;
      if (this.requestedPartyKeys.has(key)) {
        continue;
      }
      // Marked before the call so a burst of row events cannot fire twice, then released on failure:
      // initiation here is the only thing that creates the run, and the panel's Retry only pulls an
      // existing execution, so a key left behind would strand the row with no key people forever.
      this.requestedPartyKeys.add(key);
      this.subscriptions.push(
        this.kybInitiateCorporatePartyFn({
          applicationId: this.applicationId,
          parentCommercialEntityId: parent.commercialEntityId,
          role,
          legalName: (party.manualEntry ? party.businessName : party.organisation?.name) ?? null,
          abn,
          acn,
          entityType: party.organisationType?.type ?? null,
        }).pipe(
          tap(() => this.load(true)),
          catchError(() => {
            this.requestedPartyKeys.delete(key);
            return EMPTY;
          }),
        ).subscribe(),
      );
    }
  }

  /**
   * A row was removed from a partner/trustee list: drop the selections belonging to that company's
   * people. Without this the save writes individual roles for an entity it soft-deletes in the same
   * transaction.
   */
  private pruneRemovedPartySelections(
    parent: KybEntityResult,
    role: 'partner' | 'trustee',
    entries: readonly unknown[],
  ): void {
    const live = new Set<string>();
    for (const entry of entries) {
      const {abn, acn} = kybPartyIdentifiers(entry);
      if (abn) live.add(`abn:${abn}`);
      if (acn) live.add(`acn:${acn}`);
    }
    const removed = (this.result?.entities ?? []).filter(entity =>
      entity.parentCommercialEntityId === parent.commercialEntityId
      && (role === 'partner' ? entity.isPartnerOfParent : entity.isTrusteeOfParent)
      && !(entity.abn && live.has(`abn:${kybDigitsOnly(entity.abn)}`))
      && !(entity.acn && live.has(`acn:${kybDigitsOnly(entity.acn)}`)),
    );
    if (!removed.length) {
      return;
    }
    // A removed trust partner takes its nested trustee section with it, so the companies that
    // section introduced are orphaned too and must be dropped alongside their parent.
    const orphaned = [...removed];
    for (const entity of removed) {
      this.partyEntries.delete(entity.commercialEntityId);
      orphaned.push(...(this.result?.entities ?? []).filter(
        candidate => candidate.parentCommercialEntityId === entity.commercialEntityId,
      ));
    }
    for (const entity of orphaned) {
      for (const person of entity.people) {
        this.selections.delete(person.stakeholderId);
        this.selectedStakeholderIds.delete(person.stakeholderId);
      }
    }
  }

  /**
   * Corporate parties still listed in an add-party row, each mapped to the entity type its row
   * declared. An entity keeps its KYB run until the step is saved, so a company the user removed or
   * replaced (and one from a previous visit, since the rows are not re-seeded) is still in the
   * result. Those must not gate the step: they are rendered nowhere, so blocking on them leaves the
   * user with no way to see or fix the problem.
   */
  private refreshCorporateParties(): void {
    const parties = new Map<number, EntityTypes | null>();
    for (const [parentCommercialEntityId, entries] of this.partyEntries) {
      for (const entry of entries) {
        const entity = this.entityForPartyRow(parentCommercialEntityId, entry);
        if (entity) {
          parties.set(entity.commercialEntityId, this.partyEntityType(entry));
        }
      }
    }
    this.corporateParties = parties;
  }

  private entityForPartyRow(parentCommercialEntityId: number, entry: unknown): KybEntityResult | null {
    return kybFindEntityForParty(
      this.result?.entities ?? [],
      parentCommercialEntityId,
      kybPartyIdentifiers(entry),
    );
  }

  /**
   * Re-seed the add-party rows from the parties saved on a previous visit, so a reopened draft shows
   * them instead of an empty form.
   *
   * Must run before rebuildPartyControls, which reads these rows both to give each new control its
   * initial value and, via refreshCorporateParties, to decide which corporate parties are still
   * listed: with no rows a restored corporate partner/trustee matches nothing and renders nowhere.
   */
  private seedPartyEntries(result: KybResult): void {
    for (const group of result.addedParties ?? []) {
      const entity = result.entities.find(candidate => candidate.commercialEntityId === group.commercialEntityId);
      // An additional guarantor's parties are entered on the Additional Guarantors step, and the read
      // is shared by both screens. Adopting its rows here would register the corporate parties they
      // introduce as this screen's own, so they would gate a step that renders neither of them.
      if (!entity || kybIsAdditionalGuarantor(entity)) {
        continue;
      }
      const entityType = this.effectiveEntityType(entity);
      const parties = kybAddedPartiesToPartyValue(group.parties, entityType);
      if (!parties?.length) {
        continue;
      }
      this.partyEntries.set(group.commercialEntityId, parties);
      // A restored corporate party already has a run, so suppress the initiate call its row would
      // otherwise trigger on load. A row whose run is missing is deliberately left out, so it still
      // gets one rather than sitting there with no key people forever.
      const role: 'partner' | 'trustee' = entityType === 'trust' ? 'trustee' : 'partner';
      for (const party of parties) {
        if (!this.entityForPartyRow(group.commercialEntityId, party)) {
          continue;
        }
        const {abn, acn} = kybPartyIdentifiers(party);
        this.requestedPartyKeys.add(`${group.commercialEntityId}|${role}|${abn ?? ''}|${acn ?? ''}`);
      }
    }
  }

  // The entity type an add-party row carries, as resolved by the ABN/ACN lookup that populated it.
  private partyEntityType(entry: unknown): EntityTypes | null {
    return (entry as { organisationType?: { type?: EntityTypes } } | null)?.organisationType?.type ?? null;
  }

  onPersonRoleChange(selection: KybPersonRoleSelection): void {
    this.selections.set(selection.stakeholderId, selection);
    this.mirrorGuarantorToDuplicates(selection);
    this.mirrorToSameCompanyAliases(selection.stakeholderId);
    this.emitValue();
  }

  // The registry can list one person as several stakeholders of the same company, all resolving to
  // one application person (shared individualId). Select and signer are per company, so a change on
  // one row has to land on its aliases there or the screen would offer states the save cannot keep.
  // Never crosses companies: the same person may be ticked on one company and not on another.
  // Identity here is the link only; two rows that merely share a name are not treated as one.
  private mirrorToSameCompanyAliases(sourceStakeholderId: number): void {
    const source = this.findPerson(sourceStakeholderId);
    const entity = this.result?.entities.find(candidate =>
      candidate.people.some(person => person.stakeholderId === sourceStakeholderId));
    if (!source || !entity || source.individualId == null) {
      return;
    }
    const aliases = entity.people.filter(person =>
      person.stakeholderId !== sourceStakeholderId && person.individualId === source.individualId);
    if (!aliases.length) {
      return;
    }
    const entityType = this.effectiveEntityType(entity);
    const selected = this.selectedStakeholderIds.has(sourceStakeholderId);
    const sourceSelection = this.selections.get(sourceStakeholderId);
    const signer = sourceSelection?.isSigner ?? source.isSigner;
    let changed = false;
    for (const alias of aliases) {
      if (kybIsPersonSignerEligible(entity.people, source) && alias.isSigner !== signer) {
        alias.isSigner = signer;
        this.selections.set(alias.stakeholderId, {
          stakeholderId: alias.stakeholderId,
          isGuarantor: this.selections.get(alias.stakeholderId)?.isGuarantor ?? alias.isGuarantor,
          isSigner: signer,
        });
        changed = true;
      }
      if (selected) {
        this.selectedStakeholderIds.add(alias.stakeholderId);
      } else if (!kybIsSelectLocked(entityType, alias.ownershipPercent) && !alias.isGuarantor && !alias.isSigner) {
        this.selectedStakeholderIds.delete(alias.stakeholderId);
      } else {
        continue;
      }
      this.entitySections?.forEach(section =>
        section.applyMirroredSelection(alias.stakeholderId, this.selectedStakeholderIds.has(alias.stakeholderId)));
    }
    if (changed && this.result) {
      // New references so the OnPush sections re-render the mirrored signer ticks.
      this.result = {
        ...this.result,
        entities: this.result.entities.map(e => ({...e, people: e.people.map(p => ({...p}))})),
      };
    }
  }

  // isGuarantor is a person-level flag (one ApplicationIndividual), but the same human can be a KYB
  // stakeholder of several entities and so appear on multiple rows. When guarantor is toggled on one
  // row, mirror the new value onto every other row for the same person so they never diverge, and
  // keep those rows selected: a guarantor that is not selected would be treated as deselected by the
  // save and its person-level flag cleared.
  private mirrorGuarantorToDuplicates(source: KybPersonRoleSelection): void {
    if (!this.result) {
      return;
    }
    const sourcePerson = this.findPerson(source.stakeholderId);
    if (!sourcePerson) {
      return;
    }
    let changed = false;
    for (const entity of this.result.entities) {
      for (const person of entity.people) {
        const isSource = person.stakeholderId === source.stakeholderId;
        if (!isSource && !kybIsSamePerson(sourcePerson, person)) {
          continue;
        }
        if (!isSource) {
          if (person.isGuarantor === source.isGuarantor) {
            continue;
          }
          person.isGuarantor = source.isGuarantor;
          const current = this.selections.get(person.stakeholderId);
          this.selections.set(person.stakeholderId, {
            stakeholderId: person.stakeholderId,
            isGuarantor: source.isGuarantor,
            isSigner: current?.isSigner ?? person.isSigner,
          });
          changed = true;
        }
        if (source.isGuarantor || person.isSigner) {
          this.selectedStakeholderIds.add(person.stakeholderId);
        } else if (!kybIsSelectLocked(this.effectiveEntityType(entity), person.ownershipPercent)) {
          const wasSelected = person.isSelected
            ?? kybIsSelectedByDefault(this.effectiveEntityType(entity), person.ownershipPercent);
          if (wasSelected) {
            this.selectedStakeholderIds.add(person.stakeholderId);
          } else {
            this.selectedStakeholderIds.delete(person.stakeholderId);
          }
        }
      }
    }
    if (changed) {
      // New references so the OnPush entity sections re-render the mirrored guarantor (and the
      // role-driven select) checkboxes on the duplicate rows.
      this.result = {
        ...this.result,
        entities: this.result.entities.map(e => ({...e, people: e.people.map(p => ({...p}))})),
      };
    }
  }

  // On load the same person can appear on more than one entity's rows while reading one shared
  // isGuarantor, so reconcile with OR semantics: if any row for a person is a guarantor, all are.
  private reconcileLoadedGuarantors(): void {
    if (!this.result) {
      return;
    }
    const rows = this.result.entities.flatMap(entity => entity.people.map(person => ({entity, person})));
    for (const {person} of rows) {
      if (person.isGuarantor) {
        continue;
      }
      if (rows.some(r => r.person !== person && kybIsSamePerson(r.person, person) && r.person.isGuarantor)) {
        person.isGuarantor = true;
      }
    }
    for (const {person} of rows) {
      const selection = this.selections.get(person.stakeholderId);
      if (selection && selection.isGuarantor !== person.isGuarantor) {
        this.selections.set(person.stakeholderId, {...selection, isGuarantor: person.isGuarantor});
      }
      if (person.isGuarantor) {
        this.selectedStakeholderIds.add(person.stakeholderId);
      }
    }
  }

  private findPerson(stakeholderId: number): KybPerson | null {
    for (const entity of this.result?.entities ?? []) {
      const found = entity.people.find(person => person.stakeholderId === stakeholderId);
      if (found) {
        return found;
      }
    }
    return null;
  }

  onPersonSelectedChange(event: { person: { stakeholderId: number }; selected: boolean }): void {
    const stakeholderId = event.person.stakeholderId;
    if (event.selected) {
      this.selectedStakeholderIds.add(stakeholderId);
    } else {
      this.selectedStakeholderIds.delete(stakeholderId);
    }
    this.mirrorToSameCompanyAliases(stakeholderId);
    this.emitValue();
  }

  partyControlFor(entity: KybEntityResult): KybPartyControl | null {
    return this.partyControls.get(entity.commercialEntityId)?.control ?? null;
  }

  /**
   * The corporate party behind one partner/trustee row, when that party is itself a trust or a
   * partnership. Such a row renders its own trustee/partner entry section: neither has directors or
   * shareholders for FrankieOne to return, so its signers come from the parties entered there - the
   * same substitution the primary entity already makes, one level down. Resolving per row rather
   * than per parent keeps the section inside the card it belongs to, and makes it disappear with
   * that row.
   */
  nestedPartyForRow(parent: KybEntityResult, row: FormGroup): KybEntityResult | null {
    const entity = this.resolveSignerEntity(parent, row)?.entity ?? null;
    if (!entity || !kybIsCorporateParty(entity)) {
      return null;
    }
    const entityType = this.effectiveEntityType(entity);
    return entityType === 'trust' || entityType === 'partnership' ? entity : null;
  }

  /**
   * `preserveUserState` keeps guarantor/signer ticks made since the last fetch. Polling for a pending
   * corporate party re-runs this, and without it a poll would silently discard the selections the user
   * has already made on the resolved sections.
   */
  private applyResult(result: KybResult, preserveUserState = false): void {
    const previousSelections = preserveUserState ? new Map(this.selections) : null;
    const previousSelectedIds = preserveUserState ? new Set(this.selectedStakeholderIds) : null;

    if (!preserveUserState) {
      this.manualEntryEntityIds = new Set<number>();
      this.manualPeopleByEntity = new Map<number, KybManualPerson[]>();
      this.manualValidityByEntity = new Map<number, boolean>();
    }
    for (const entity of result.entities) {
      // An additional guarantor's manual people are entered and saved by the Additional Guarantors
      // step, which contributes its own manualEntities to the merged payload. Claiming it here as
      // well sends a second, empty entry for the same entity, which the save rejects.
      if (kybIsAdditionalGuarantor(entity)) {
        continue;
      }
      const savedEmptyResultEntry = kybHasManualPeopleForEmptyResult(entity);
      if (entity.mode === 'MANUAL'
        || this.manualEntryEntityIds.has(entity.commercialEntityId)
        || savedEmptyResultEntry) {
        // Partner/trustee children currently use entity.mode to choose their embedded form. This is
        // view state only; persistence derives manual mode from whether a KYB result exists.
        entity.mode = 'MANUAL';
        this.manualEntryEntityIds.add(entity.commercialEntityId);
        if (!this.manualPeopleByEntity.has(entity.commercialEntityId)) {
          this.manualPeopleByEntity.set(entity.commercialEntityId, entity.manualPeople ?? []);
        }
        const people = this.manualPeopleByEntity.get(entity.commercialEntityId) ?? [];
        this.manualValidityByEntity.set(
          entity.commercialEntityId,
          people.length > 0 && people.some(person => person.isSigner),
        );
      }
    }
    this.result = result;
    this.selections = new Map();
    this.selectedStakeholderIds = new Set<number>();
    if (!preserveUserState) {
      this.partyControls = new Map();
      this.partyEntries = new Map();
      this.corporateParties = new Map();
      this.signerLookups = new Map();
      this.requestedPartyKeys = new Set<string>();
      this.seedPartyEntries(result);
    }
    for (const entity of result.entities) {
      // Ordered once here rather than per render, so both the section table and the "Select signers"
      // table inside a partner/trustee row - which read the same entity - agree on the order.
      entity.people = kybSortKeyPeople(entity.people);
      for (const person of entity.people) {
        this.selections.set(person.stakeholderId, {
          stakeholderId: person.stakeholderId,
          isGuarantor: person.isGuarantor,
          isSigner: person.isSigner,
        });
      }
    }
    this.rebuildPartyControls();
    this.seedSelectedDefaults(result);
    this.applySoleTraderRoles();
    this.reconcileLoadedGuarantors();
    this.restoreUserState(previousSelections, previousSelectedIds);
    this.emitValue();
  }

  // Runs after rebuildPartyControls, which settles the corporate-party types effectiveEntityType reads.
  private applySoleTraderRoles(): void {
    for (const entity of this.result?.entities ?? []) {
      if (!this.isSoleTraderRoleLocked(entity)) {
        continue;
      }
      for (const person of entity.people) {
        person.isGuarantor = true;
        person.isSigner = true;
        this.selections.set(person.stakeholderId, {
          stakeholderId: person.stakeholderId,
          isGuarantor: true,
          isSigner: true,
        });
        this.selectedStakeholderIds.add(person.stakeholderId);
      }
    }
  }

  // Re-apply ticks the user made before a poll, for people still present in the new result.
  private restoreUserState(
    previousSelections: Map<number, KybPersonRoleSelection> | null,
    previousSelectedIds: Set<number> | null,
  ): void {
    if (!previousSelections || !previousSelectedIds) {
      return;
    }
    for (const entity of this.result?.entities ?? []) {
      for (const person of entity.people) {
        const previous = previousSelections.get(person.stakeholderId);
        if (!previous) {
          continue;
        }
        person.isGuarantor = previous.isGuarantor;
        person.isSigner = previous.isSigner;
        this.selections.set(person.stakeholderId, previous);
        if (previousSelectedIds.has(person.stakeholderId)) {
          this.selectedStakeholderIds.add(person.stakeholderId);
        } else if (!kybIsSelectLocked(this.effectiveEntityType(entity), person.ownershipPercent)) {
          this.selectedStakeholderIds.delete(person.stakeholderId);
        }
      }
    }
  }

  // Ensures every partnership/trust entity (per the effective type, which follows organisationType
  // for the primary entity) has an add-party control. Preserves controls already created so
  // user-entered data survives an org-type change; drops controls no longer applicable.
  private rebuildPartyControls(): void {
    this.refreshCorporateParties();
    if (!this.result) {
      return;
    }
    const next: Map<number, { entity: KybEntityResult; control: KybPartyControl }> = new Map();
    for (const entity of this.result.entities) {
      const entityType = this.effectiveEntityType(entity);
      if (entityType !== 'partnership' && entityType !== 'trust') {
        continue;
      }
      // An additional guarantor is filtered out of the sections this screen renders (see
      // topLevelEntities), so its add-party form appears nowhere and its control could never be
      // filled in. Covers the corporate parties below it too: those inherit the guarantor role from
      // their owner, so they answer to this the same way.
      if (kybIsAdditionalGuarantor(entity)) {
        continue;
      }
      // A corporate party gets an add-party component only while it is still listed on the screen.
      // One left behind by a removed row renders nowhere, so a control for it could never be filled
      // in and would block the step.
      if (kybIsCorporateParty(entity) && !this.corporateParties.has(entity.commercialEntityId)) {
        continue;
      }
      const existing = this.partyControls.get(entity.commercialEntityId);
      if (existing) {
        next.set(entity.commercialEntityId, existing);
      } else {
        // Seeded at construction rather than set afterwards: the partner/trustee forms build a row
        // per value written to them, so a second write would duplicate every restored party.
        const control: KybPartyControl = new FormControl<PartnerValue | TrusteeValue | null>(
          this.seededPartyValue(entity),
        );
        this.subscriptions.push(control.valueChanges.pipe(tap(() => this.emitValue())).subscribe());
        next.set(entity.commercialEntityId, {entity, control});
      }
    }
    this.partyControls = next;
  }

  // Rows a restored add-party form starts from; null leaves a first-time form on its own defaults
  // (two blank partners / one blank trustee).
  private seededPartyValue(entity: KybEntityResult): PartnerValue | TrusteeValue {
    const seeded = this.partyEntries.get(entity.commercialEntityId);
    return seeded?.length ? (seeded as Exclude<PartnerValue, null>) : null;
  }

  // Left-column select state for every person, from the strongest source available: a locked
  // selection, then what a previous save recorded, then the entity-type default.
  private seedSelectedDefaults(result: KybResult): void {
    this.selectedStakeholderIds = new Set<number>();
    for (const entity of result.entities) {
      const entityType = this.effectiveEntityType(entity);
      for (const person of entity.people) {
        // A locked selection is not the user's to change, so it wins over anything persisted.
        if (kybIsSelectLocked(entityType, person.ownershipPercent)) {
          this.selectedStakeholderIds.add(person.stakeholderId);
          continue;
        }
        // A saved tick takes precedence over the type-based default, so a person the user selected on
        // a previous visit comes back selected. A guarantor/signer stays ticked either way: the save
        // clears the roles of anyone unselected, which would otherwise silently drop them.
        const selected = (person.isSelected ?? kybIsSelectedByDefault(entityType, person.ownershipPercent))
          || person.isGuarantor || person.isSigner;
        if (selected) {
          this.selectedStakeholderIds.add(person.stakeholderId);
        }
      }
    }
  }

  // Only a party whose own key people come from FrankieOne can gate the step. A trust or partnership
  // party has nothing to wait for - its signers come from the nested section the user fills in - so
  // the state of its run must not block.
  private pendingCorporateParties(): KybEntityResult[] {
    return (this.result?.entities ?? []).filter(entity => {
      if (!this.corporateParties.has(entity.commercialEntityId)) {
        return false;
      }
      const entityType = this.effectiveEntityType(entity);
      return entityType !== 'trust' && entityType !== 'partnership' && this.statusFor(entity) !== 'resolved';
    });
  }

  // Same population as runValidation plus sole traders: resolved entities this step actually shows,
  // minus those already in Manual KYB. An empty completed run cannot provide a signer, so Next is
  // held until the broker enters people by hand rather than raising an unsatisfiable "select a signer".
  private emptyPeopleEntities(): KybEntityResult[] {
    return (this.result?.entities ?? []).filter(entity => {
      if (this.isManualEntry(entity) || entity.people.length) {
        return false;
      }
      const entityType = this.effectiveEntityType(entity);
      if ((entityType !== 'company' && entityType !== 'sole-trader') || this.statusFor(entity) !== 'resolved') {
        return false;
      }
      if (kybIsAdditionalGuarantor(entity)) {
        return false;
      }
      if (kybIsCorporateParty(entity) && !this.corporateParties.has(entity.commercialEntityId)) {
        return false;
      }
      return true;
    });
  }

  private computeDisplayStatus(result: KybResult | null): KybDisplayStatus {
    if (kybResultResolved(result)) {
      return 'resolved';
    }
    if (kybResultFailed(result)) {
      return 'failed';
    }
    return 'pending';
  }

  private primaryIdentifiers(): KybPartyIdentifiers | null {
    const primary = this.result?.entities.find(entity => entity.isPrimary);
    return primary ? {abn: primary.abn, acn: primary.acn} : null;
  }

  private collectPartyIntegrityErrors(): string[] {
    const primary = this.primaryIdentifiers();
    const errors: { message: string }[] = [];
    for (const [parentId, {entity}] of this.partyControls) {
      const role = this.effectiveEntityType(entity) === 'trust' ? 'trustee' : 'partner';
      for (const entry of this.partyEntries.get(parentId) ?? []) {
        errors.push(...kybPartyEntryIntegrityErrors(entry, role, primary));
      }
    }
    return uniqueIntegrityMessages(errors);
  }

  private emitValue(): void {
    this.runValidation();
    this.blockingReason = null;
    this.partyIntegrityErrors = this.collectPartyIntegrityErrors();
    // Only the primary entity gates the step; a resolved primary section keeps emitting so the rest
    // of the step stays usable while another section is still running.
    if (this.primaryStatus() !== 'resolved') {
      this.propagateChange(null);
      return;
    }
    if (this.partyIntegrityErrors.length) {
      this.propagateChange(null);
      return;
    }
    // A corporate party that has not resolved blocks too, because its signers cannot be chosen yet.
    const pending = this.pendingCorporateParties();
    if (pending.length) {
      const names = pending.map(entity => (entity.entityName ?? '').trim() || 'a company you added').join(', ');
      this.blockingReason = `We are still confirming the key people for ${names}. ` +
        `Open "Select signers" for that partner or trustee to see its progress, then continue once it completes.`;
      this.propagateChange(null);
      return;
    }
    // A party control is null while its own form is invalid, so an incomplete partner/trustee list
    // must not pass step 4 - without this, Validators.required is satisfied by the wrapper value.
    const partyControlsInvalid = Array.from(this.partyControls.values())
      .some(({control}) => control.invalid || control.value == null);
    if (partyControlsInvalid) {
      this.blockingReason = 'Complete the details of every partner/trustee listed above before continuing.';
      this.propagateChange(null);
      return;
    }
    const invalidManualEntity = Array.from(this.manualEntryEntityIds)
      .find(entityId => !this.manualValidityByEntity.get(entityId));
    if (invalidManualEntity != null) {
      this.blockingReason = 'Complete every manually entered person and select at least one signer before continuing.';
      this.propagateChange(null);
      return;
    }
    const emptyPeople = this.emptyPeopleEntities();
    if (emptyPeople.length) {
      this.blockingReason = kybEmptyPeopleManualEntryReason(
        emptyPeople.map(entity => entity.entityName ?? ''),
      );
      this.propagateChange(null);
      return;
    }
    if (this.hasValidationErrors()) {
      // No blocking reason of its own: each failing rule already renders against the section it
      // belongs to, so a summary here would only repeat them.
      this.propagateChange(null);
      return;
    }
    const addedParties: KybAddedParties[] = Array.from(this.partyControls.values()).map(({entity, control}) => ({
      commercialEntityId: entity.commercialEntityId,
      entityType: this.effectiveEntityType(entity),
      parties: kybNormalizePartyDobForSave(control.value),
    }));
    const value: KybKeyPeopleValue = {
      selections: Array.from(this.selections.values()),
      selectedStakeholderIds: Array.from(this.selectedStakeholderIds.values()),
      addedParties,
      manualEntities: Array.from(this.manualEntryEntityIds).map(commercialEntityId => ({
        commercialEntityId,
        people: this.manualPeopleByEntity.get(commercialEntityId) ?? [],
      })),
    };
    this.propagateChange(value);
  }

  // Company rules run per resolved company section that this step actually shows: the primary
  // borrower and any corporate partners/trustees still listed in an add-party row. Additional
  // guarantors (previous business, etc.) are filtered out of the UI here and validated on the
  // Additional Guarantors step instead — validating them here blocks Next with errors the broker
  // cannot see or fix on this screen.
  private runValidation(): void {
    const next = new Map<number, KybValidationResult>();
    for (const entity of this.result?.entities ?? []) {
      if (this.isManualEntry(entity)) {
        continue;
      }
      if (this.effectiveEntityType(entity) !== 'company' || this.statusFor(entity) !== 'resolved') {
        continue;
      }
      if (!entity.people.length) {
        continue;
      }
      if (kybIsAdditionalGuarantor(entity)) {
        continue;
      }
      if (kybIsCorporateParty(entity) && !this.corporateParties.has(entity.commercialEntityId)) {
        continue;
      }
      next.set(
        entity.commercialEntityId,
        kybValidateCompanyKeyPeople(entity, 'company', this.effectiveSelections()),
      );
    }
    this.validationByEntity = next;
    this.warningsChange.emit(this.collectWarnings());
  }

  // Mirrors the save path, which only persists roles for selected stakeholders: counting an
  // unselected person's ticks here would let the step pass and the save reject it afterwards.
  private effectiveSelections(): KybPersonRoleSelection[] {
    return Array.from(this.selections.values()).map(selection =>
      this.selectedStakeholderIds.has(selection.stakeholderId)
        ? selection
        : {...selection, isGuarantor: false, isSigner: false});
  }

  // One message per distinct warning, prefixed with the entity name only when it is known, so a
  // corporate partner/trustee is identifiable without a nameless entity producing a bare ": ".
  private collectWarnings(): string[] {
    const messages: string[] = [];
    for (const entity of this.result?.entities ?? []) {
      const name = (entity.entityName ?? '').trim();
      const prefix = !entity.isPrimary && name ? `${name}: ` : '';
      for (const warning of this.validationByEntity.get(entity.commercialEntityId)?.warnings ?? []) {
        const message = `${prefix}${warning}`;
        if (!messages.includes(message)) {
          messages.push(message);
        }
      }
    }
    return messages;
  }

  private hasValidationErrors(): boolean {
    return Array.from(this.validationByEntity.values()).some(v => v.errors.length > 0);
  }

  errorsFor(entity: KybEntityResult): string[] {
    return this.showValidation ? this.validationByEntity.get(entity.commercialEntityId)?.errors ?? [] : [];
  }

  // A corporate party's errors belong next to its own signers table, not the parent's section.
  corporatePartyErrorsFor(parent: KybEntityResult): { entity: KybEntityResult; errors: string[] }[] {
    return this.corporatePartyDescendantsOf(parent)
      .map(entity => ({entity, errors: this.errorsFor(entity)}))
      .filter(item => item.errors.length > 0);
  }

  /**
   * Every corporate party below a section, not just its direct children: the trustee of a trust
   * partner hangs off the trust partner rather than off the section, and its errors would otherwise
   * block the step from a row that reports nothing.
   */
  private corporatePartyDescendantsOf(parent: KybEntityResult): KybEntityResult[] {
    const entities = this.result?.entities ?? [];
    const descendants: KybEntityResult[] = [];
    const queue: number[] = [parent.commercialEntityId];
    while (queue.length) {
      const parentId = queue.shift() as number;
      for (const entity of entities) {
        if (entity.parentCommercialEntityId !== parentId
          || !kybIsCorporateParty(entity)
          || descendants.includes(entity)) {
          continue;
        }
        descendants.push(entity);
        queue.push(entity.commercialEntityId);
      }
    }
    return descendants;
  }

  doWriteValue(_v?: KybKeyPeopleValue | null): void {
    // No writable controls remain on this component; guarantors moved to a separate page.
  }

  mark(): void {
    this.showValidation = true;
    this.markObservable.next(true);
    this.manualPeopleComponents?.forEach(component => component.mark());
  }
}
